import { 
	ShareBaseNotification
} from 'phoenix/domain';
import { TokenStringFilter } from 'phoenix/filters/tokenString';
import { Serializable } from 'util/domain';
import { CommonUtilsService } from 'util/services';

interface NotificationToken {
	phrase:string;
	tuple?: {
		key: string;
		value: any;
	};	
	link?:string;
	isPunctuation:boolean;	
}   

export class ClientNotification extends Serializable<ShareBaseNotification> {
	public action:string;
	public tokens: NotificationToken[];
	public Unread:boolean = false;
	public id: string;
	public Message:string;
	public NotificationDate:string;
	SelfLink:string;

	constructor(
		private sbNotification: ShareBaseNotification, 
		private $state, 
		private utils:CommonUtilsService, 
		private pxTokenStringFilter:TokenStringFilter,
		source:ShareBaseNotification = angular.copy(sbNotification)
	){
		super(source);
		let tokens = this.assembleTokens(sbNotification.Message);
		let action = tokens.filter(t => t.tuple && t.tuple.key === 'Action')[0];
		let actionName = action ? action.tuple.value : null;

		this.action = actionName;
		this.tokens = tokens;
		// This part of the notification object is the only part that marks it as unique
		// Notification.Links.Self = "http://{client-machine}/{api}/notifications/{id}"
        this.id =  this.utils.generateHash(sbNotification.Links.Self);
		this.Message = this.pxTokenStringFilter(sbNotification.Message);
		if(this.action === 'revoked'){
			this.tokens.forEach(t => {
				t.link = null;
			});
		}
		this.SelfLink = sbNotification.Links.Self;
	}

	public toApi(): ShareBaseNotification {
		return this.source;
	}

	private assembleTokens(tokenString:string) {
		// https://regex101.com/r/ovuuNO/3/
		const mentionRegex = /([^[\]@\/.!?]*)\[@(\w+=\w+)\]([^[\]@\/]+)\[\/\]([^[\]@\/\s\\\n]*)/ig;
		let group;
		let tokens:NotificationToken[] = [];
		let message = angular.copy(tokenString);

		while(group = mentionRegex.exec(tokenString)) {
			// This is necessary to avoid infinite loops with zero-width matches
			if (group.index === mentionRegex.lastIndex) {
				mentionRegex.lastIndex++;
			}

			// matches start at 1 and have 5 potential pieces 
			for( var i = 1; i <= 4; i++) {
				let match = group[i].trim();
				if(match.length < 1){
					continue;
				}

				let token:NotificationToken = {
					phrase: match,
					tuple: null,
					isPunctuation: /^[.!?]$/g.test(match)
				};

				// the second and third capture group should have the mention mark up
				if(i === 2){
					let tuple = match.split('=');
					token.tuple = {
						key: tuple[0],
						value: tuple[1]
					};
					token.phrase = group[++i].trim();
					token.link = this.getTokenLink(token);
				}
			
				tokens.push(token);						
			}
		}

		return tokens;
	}

	private getTokenLink(t:NotificationToken) {
		let key = t.tuple.key.toLowerCase();
		let value = t.tuple.value;
		let config = {
			absolute: false
		};
		switch (key) {
			case 'library':
				return this.$state.href('content.shared.library', { 'libraryID': value }, config);
			case 'folder':
				return this.$state.href('content.myFiles.folder', { 'folderID': value }, config);
			case 'document':
				return this.$state.href('content.myFiles.document', { 'docID':value }, config);
			default:
				return null;
		}
	}

	public get isTaskNotification() {
		return this.tokens.some(t => t.tuple && t.tuple.key === 'Task');
	}

	public get icon() {
		var icon = NotificationIcon.Info;
	
		if(this.action in NotificationActionMappings){
			icon = NotificationActionMappings[this.action];
		}
		else if (this.isTaskNotification) {
			icon = NotificationIcon.AssignmentTurnedIn;
		}
	
		return icon;
	}
}

export enum NotificationAction {
	Granted = 'granted',
	MemberAdded = 'memberAdded',
	MemberRemoved = 'memberRemoved',
	Mentioned = 'mentioned' ,
	PasswordChanged = 'passwordChanged',
	Revoked = 'revoked'
};


export enum NotificationTaskAction {
	TaskCreated = 'taskCreated',
	TaskCompleted = 'taskCompleted',
	TaskCancelled = 'taskCancelled',
	TaskDeleted = 'taskDeleted'
};

enum NotificationIcon {
	AssignmentTurnedIn = 'assignment_turned_in',
	Group = 'group',
	Info = 'info',
	Lock = 'lock',
	ModeComment = 'mode_comment',
	NotInterested = 'not_interested',
	PersonAdd = 'person_add'
}

const NotificationActionMappings = {
	[NotificationAction.Granted] : NotificationIcon.PersonAdd,
	[NotificationAction.MemberAdded] : NotificationIcon.Group,
	[NotificationAction.MemberRemoved] : NotificationIcon.Group,
	[NotificationAction.Mentioned] : NotificationIcon.ModeComment,
	[NotificationAction.PasswordChanged] : NotificationIcon.Lock,
	[NotificationAction.Revoked] : NotificationIcon.NotInterested
};


// WEBPACK FOOTER //
// ./src/sharebase/domain/client-notification.ts